import { X, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

interface AuctionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  icon?: React.ReactNode;
  isLoading?: boolean;
}

export function AuctionConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Bekreft",
  cancelLabel = "Avbryt",
  variant = "default",
  icon,
  isLoading
}: AuctionConfirmModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md animate-in fade-in duration-300">
      <Card className="w-full max-w-md shadow-2xl border-border/50 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 text-center space-y-4">
          <div className="flex justify-center">
            <div className={`p-4 rounded-full ${variant === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
              {icon || <AlertCircle className="h-8 w-8" />}
            </div>
          </div>
          
          <div className="space-y-2">
            <h3 className="text-xl font-black tracking-tight">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {description}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 p-4 bg-muted/30 border-t border-border/50">
          <Button 
            variant="ghost" 
            className="flex-1 h-11 font-bold" 
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button 
            variant={variant === "destructive" ? "destructive" : "default"}
            className={`flex-1 h-11 font-bold shadow-lg ${variant === 'default' ? 'shadow-primary/20' : 'shadow-destructive/20'}`}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Behandler..." : confirmLabel}
          </Button>
        </div>
        
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-muted text-muted-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </Card>
    </div>
  );
}
